/**
 * Phase 14 — Frischer Blob-Lesepfad aus IndexedDB (Cross-Tab / Optimistic Lock).
 */

import {
  businessDataStorageKey,
  invalidateMergedPayloadSessionCache,
  listBusinessMergedPayloadKeyCandidates,
  normalizeBusinessStorageId,
  payloadRecencyMeta
} from '../../utils/sharedCore';
import { getStorageEngineRef } from './storageEngineRef';

export function isBusinessPayloadOptimisticLockEnabled() {
  const v = process.env.REACT_APP_BUSINESS_PAYLOAD_OPTIMISTIC_LOCK;
  if (v === 'false' || v === '0') return false;
  try {
    return Boolean(getStorageEngineRef().usesIndexedDb?.());
  } catch {
    return false;
  }
}

/**
 * Lädt den kanonischen business_*_data-Key aus IDB in den Tab-Cache.
 * @param {string} businessId
 */
export async function reloadFreshBusinessPayloadFromIdb(businessId) {
  const n = normalizeBusinessStorageId(businessId);
  const key = businessDataStorageKey(n);
  if (!key) return null;

  const engine = getStorageEngineRef();
  if (engine._backend?.reloadKeyFromIdb) {
    await engine._backend.reloadKeyFromIdb(key);
  }
  invalidateMergedPayloadSessionCache(n || businessId);
  return key;
}

/**
 * Await deferred business_*_data keys before sync blob read/write.
 * With NO_KV_BLOB + deferred bulky hydration, sync kvGet returns null until
 * ensureKeysLoaded finishes — that made login unlock an empty catalog.
 * @param {string} businessId
 * @returns {Promise<{ loaded: boolean, keys: string[] }>}
 */
export async function ensureBusinessPayloadKeysLoaded(businessId) {
  const n = normalizeBusinessStorageId(businessId);
  if (!n) return { loaded: false, keys: [] };

  const keys = listBusinessMergedPayloadKeyCandidates(n);
  if (keys.length === 0) return { loaded: false, keys: [] };

  try {
    const engine = getStorageEngineRef();
    const backend = engine?._backend;
    if (typeof backend?.ensureKeysLoaded === 'function') {
      await backend.ensureKeysLoaded(keys);
    } else if (typeof backend?.reloadKeyFromIdb === 'function') {
      await Promise.all(keys.map((key) => backend.reloadKeyFromIdb(key)));
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[businessPayload] ensureBusinessPayloadKeysLoaded failed:',
        err?.message || err
      );
    }
  }

  invalidateMergedPayloadSessionCache(n);
  invalidateMergedPayloadSessionCache(businessId);
  return { loaded: true, keys };
}

/**
 * @param {object} parsed
 */
export function readPayloadLockVersion(parsed) {
  return payloadRecencyMeta(parsed).version;
}

export class BusinessPayloadWriteConflict extends Error {
  constructor() {
    super('Business payload write conflict: another tab updated the business blob');
    this.name = 'BusinessPayloadWriteConflict';
  }
}
